import * as THREE from "three";
import { LIGHT, T, TILES, isBuilt, isSolid, steamExhaust, tileShape, type ShipBody, type TurretState, type Vec2 } from "@dyson/shared";
import { GridMesh, tileMaterial } from "./GridMesh";
import { lanternGlow, type Lantern } from "./lanterns";
import { ShipScars } from "./ShipScars";
import { TurretBarrels } from "./TurretBarrels";

/** Where and how a ship is drawn this frame. */
export interface ShipPose {
  position: Vec2;
  angle: number;
  /** Which way its engines are firing (see ShipState.throttle), for their steam. */
  throttle?: Vec2;
}

/**
 * How much of a ship is drawn, by how far out the camera is: "far" a flat
 * silhouette (so the big map stays readable), "close" with each block's seams.
 */
export type ShipDetail = "far" | "normal" | "close";

/** Seen from far out, a ship is a flat dark shape; a pirate's has a bloody tinge. */
const SILHOUETTE = new THREE.Color(0.13, 0.15, 0.19);
const PIRATE_SILHOUETTE = new THREE.Color(0.32, 0.08, 0.07);

/** Faint lines along every block's edges, close up. */
const seamMaterial = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22, depthWrite: false });

/** A soft round glow, white in the middle fading to nothing (tinted by the material). */
const exhaustTexture = (() => {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.3, "rgba(255,255,255,0.45)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  return new THREE.CanvasTexture(c);
})();

/** Warm and brighter than white, so bloom makes a firing nozzle glow and it spills onto the hull round it. */
const exhaustGeometry = new THREE.PlaneGeometry(2.6, 2.6);
const exhaustMaterial = new THREE.MeshBasicMaterial({
  map: exhaustTexture,
  color: new THREE.Color(2.2, 1.0, 0.32),
  transparent: true,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
  fog: false,
});

/**
 * A ship on screen: its tile grid, and a gun barrel on each turret. `group`
 * sits at the grid centre (the pivot) and carries the rotation; `inner`
 * shifts the grid so local tile coordinates line up. Crew are placed with
 * `toWorld` so they ride along without jitter.
 *
 * Seen from outside (by anyone not aboard), everything shut in by the hull is
 * drawn as plain hull, timber or iron, whichever it's mostly built of.
 */
export class ShipView {
  readonly group = new THREE.Group();
  /** Its steam engines: grid tile and which way the exhaust points. */
  engines: { x: number; y: number; dx: number; dy: number }[] = [];
  private readonly inner = new THREE.Group();
  private readonly grid: GridMesh;
  private readonly material: THREE.MeshBasicMaterial;
  /** Stands in for `material` when the ship is drawn as a silhouette. */
  private readonly silhouette = new THREE.MeshBasicMaterial({ color: SILHOUETTE });
  private pivot = new THREE.Vector2();
  /** A swinging barrel on each turret. */
  private readonly barrels = new TurretBarrels();
  /** Cracks and flames. */
  private readonly scars = new ShipScars();
  /** Block seams, close up; built only when they're wanted. */
  private readonly seams = new THREE.LineSegments(new THREE.BufferGeometry(), seamMaterial);
  private seamsStale = true;
  /** A glow at each engine's nozzle, shown while it fires (by engine, in `engines` order). */
  private readonly exhaust: THREE.Mesh[] = [];
  private readonly exhaustGroup = new THREE.Group();
  private body?: ShipBody;
  /** Drawn from outside: its inside hidden behind the hull. */
  private covered = true;
  private detail: ShipDetail = "normal";
  /** Lanterns that shine for whoever's looking (not those shut inside, from outside). */
  private lanterns: Lantern[] = [];

  constructor() {
    this.material = tileMaterial.clone();
    this.grid = new GridMesh(this.material);
    this.seams.position.z = 0.01;
    this.seams.visible = false;
    this.inner.add(this.grid.mesh, this.barrels.group, this.scars.group, this.seams, this.exhaustGroup);
    this.group.add(this.inner);
  }

  rebuild(body: ShipBody): void {
    this.body = body;
    this.pivot.set(body.grid.w / 2, body.grid.h / 2);
    this.inner.position.set(-this.pivot.x, -this.pivot.y, 0);
    const { grid, stats } = body;
    const cover = this.covered ? { mask: stats.interior, tile: hullTile(body) } : undefined;
    this.lanterns = [];
    for (const i of stats.cells) {
      const r = TILES[grid.tiles[i]!]?.lightRadius ?? 0;
      if (r && !cover?.mask[i]) this.lanterns.push({ x: i % grid.w, y: Math.floor(i / grid.w), r });
    }
    const lanterns = this.lanterns;
    this.grid.rebuild(grid, {
      cover,
      lamp: lanterns.length ? (x, y) => LIGHT.strength * lanternGlow(lanterns, x, y) : undefined,
      // Shaped blocks are cut to their slope (see shapes.ts), and laid ground smooths to its neighbours.
      shape: (x, y) => tileShape(grid, x, y),
    });
    this.engines = [];
    for (const i of stats.cells) {
      const exhaust = steamExhaust(grid.tiles[i]!);
      if (exhaust) this.engines.push({ x: i % grid.w, y: Math.floor(i / grid.w), dx: exhaust[0], dy: exhaust[1] });
    }
    this.placeExhaust();
    this.barrels.set(grid, stats.turrets, stats.harpoons);
    this.seamsStale = true;
    if (this.detail === "close") this.buildSeams();
  }

  /** Whether we're looking from outside (its inside hidden) or aboard. Redraws if that changed. */
  setCovered(covered: boolean): void {
    if (covered === this.covered) return;
    this.covered = covered;
    if (this.body) this.rebuild(this.body);
  }

  /** Point each turret's barrel (and harpoon's launcher) where its gunner is aiming; `lineOut`: the harpoon whose line is out, if one is. */
  setTurrets(turrets: Record<string, TurretState> | undefined, lineOut: number | null = null): void {
    this.barrels.aim(turrets, lineOut);
  }

  /** Show where it's damaged and burning (`time` in seconds, for the flames' flicker). */
  setScars(damage: Record<string, number> | undefined, fires: Record<string, number> | undefined, time: number): void {
    if (this.body) this.scars.update(this.body.grid, damage, fires, time);
  }

  /** How lit it is by the suns; a pirate ship has a bloody tinge, so you know it at a glance. */
  setLight(brightness: number, pirate = false): void {
    // The suns' part of the light baked in dims; its lanterns don't (see GridMesh.setSun).
    this.grid.setSun(brightness);
    this.material.color.setRGB(1, pirate ? 0.62 : 1, pirate ? 0.55 : 1);
    this.silhouette.color.copy(pirate ? PIRATE_SILHOUETTE : SILHOUETTE);
  }

  /**
   * How much to draw, by zoom: "far" a flat silhouette with no scars or
   * barrels, "normal" as usual, "close" with block seams. Only builds what the
   * new level needs (the seams, the first time they're shown after a rebuild).
   */
  setDetail(level: ShipDetail): void {
    if (level === this.detail) return;
    this.detail = level;
    const far = level === "far";
    this.grid.mesh.material = far ? this.silhouette : this.material;
    this.scars.group.visible = !far;
    this.barrels.group.visible = !far;
    if (level === "close" && this.seamsStale) this.buildSeams();
    this.seams.visible = level === "close";
  }

  /**
   * Glow at the nozzle of every engine that's firing: those whose exhaust
   * points against the way it's being pushed (the same rule as its steam).
   */
  setExhaust(throttle: Vec2): void {
    const time = performance.now() / 1000;
    this.engines.forEach((e, k) => {
      const mesh = this.exhaust[k];
      if (!mesh) return;
      const firing = (throttle.x > 0 && e.dx < 0) || (throttle.x < 0 && e.dx > 0) || (throttle.y > 0 && e.dy < 0) || (throttle.y < 0 && e.dy > 0);
      mesh.visible = firing;
      if (!firing) return;
      // Each nozzle flickers on its own.
      const flicker = 0.85 + 0.1 * Math.sin(time * 23 + k * 2.1) + 0.06 * Math.sin(time * 57 + k);
      mesh.scale.setScalar(flicker);
    });
  }

  /** Place the ship (pose arrives already smoothed). */
  setPose(pose: ShipPose): void {
    this.group.position.set(pose.position.x + this.pivot.x, pose.position.y + this.pivot.y, 0);
    this.group.rotation.z = pose.angle;
    this.group.updateMatrixWorld();
  }

  get angle(): number {
    return this.group.rotation.z;
  }

  /** Ship-local grid coordinates to world, as currently drawn. */
  toWorld(x: number, y: number, out: THREE.Vector3): THREE.Vector3 {
    return this.inner.localToWorld(out.set(x, y, 0));
  }

  setFire(on: boolean): void {
    this.grid.setFire(on);
  }

  dispose(): void {
    this.grid.dispose();
    this.material.dispose();
    this.silhouette.dispose();
    this.seams.geometry.dispose();
  }

  /** One exhaust glow per engine, just off its nozzle and just in front of the tiles; hidden until it fires. */
  private placeExhaust(): void {
    while (this.exhaust.length < this.engines.length) {
      const mesh = new THREE.Mesh(exhaustGeometry, exhaustMaterial);
      this.exhaust.push(mesh);
      this.exhaustGroup.add(mesh);
    }
    this.exhaust.forEach((mesh, k) => {
      const e = this.engines[k];
      mesh.visible = false;
      if (e) mesh.position.set(e.x + 0.5 + e.dx * 0.6, e.y + 0.5 + e.dy * 0.6, 0.06);
    });
  }

  /**
   * Lines round every solid block as drawn (inside covered over as hull):
   * each square one's left and bottom edges, and its right and top where
   * nothing solid continues it, so shared edges are drawn once. Shaped blocks
   * are outlined along their slope.
   */
  private buildSeams(): void {
    this.seamsStale = false;
    const body = this.body;
    if (!body) return;
    const { grid, stats } = body;
    const hull = this.covered ? hullTile(body) : T.EMPTY;
    const shown = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= grid.w || y >= grid.h) return T.EMPTY;
      const i = y * grid.w + x;
      return this.covered && stats.interior[i] ? hull : grid.tiles[i]!;
    };
    const pos: number[] = [];
    const line = (x0: number, y0: number, x1: number, y1: number) => pos.push(x0, y0, 0, x1, y1, 0);
    for (const i of stats.cells) {
      const x = i % grid.w;
      const y = Math.floor(i / grid.w);
      const t = shown(x, y);
      if (!isSolid(t)) continue;
      const shape = tileShape(grid, x, y);
      if (shape) {
        for (const [p, q] of shape.surface) line(x + p.x, y + p.y, x + q.x, y + q.y);
        continue;
      }
      line(x, y, x, y + 1);
      line(x, y, x + 1, y);
      if (!isSolid(shown(x + 1, y))) line(x + 1, y, x + 1, y + 1);
      if (!isSolid(shown(x, y + 1))) line(x, y + 1, x + 1, y + 1);
    }
    const g = this.seams.geometry;
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pos), 3));
    g.computeBoundingSphere();
  }
}

/** What a ship's hull is mostly made of (timber or iron plate, in whichever family): its inside, from outside, looks like that. */
function hullTile(body: ShipBody): number {
  const count = new Map<number, number>();
  for (const i of body.stats.cells) {
    const t = body.grid.tiles[i]!;
    if (isBuilt(t, "plank") || isBuilt(t, "iron")) count.set(t, (count.get(t) ?? 0) + 1);
  }
  let best: number = T.SHIPWRIGHT_PLANK;
  for (const [t, n] of count) if (n > (count.get(best) ?? 0)) best = t;
  return best;
}
